import * as React from 'react';
import { useState, useEffect } from 'react';
import { Table } from 'react-bootstrap';
import CustomerInvoicablesListRow from './CustomerInvoicablesListRow';
import { Translate } from '../../shared/LanguageService';
import PendingInvoiceListRow from './PendingInvoiceListRow';
import { getOrderCssClass } from '../../shared/Site';

interface ICustomerInvoicablesListProps {
    items: any[];
    showInvoice: (brokerFirmId: any, invoiceCheckSum: any) => void;
    pendingInvoices: any[];
    tryInvoiceAgain: (invoiceId: number) => void;
    onSelectedItemsChange: (selectedItems: any[]) => void;
}

const CustomerInvoicablesList = (props: ICustomerInvoicablesListProps) => {
    const [filterOrder, updateFilterOrder] = useState(1);
    const [filterColumn, updateFilterColumn] = useState('brokerFirmName');
    const [allChecked, setAllChecked] = useState(false);
    const [checkedItems, setCheckedItems] = useState<{ [key: number]: boolean }>({});
    const items = [...props.items];

    useEffect(() => {
        const initialCheckedItems = props.items.reduce((acc, item) => {
            acc[item.id] = false;
            return acc;
        }, {} as { [key: number]: boolean });
        setCheckedItems(initialCheckedItems);
    }, [props.items]);

    useEffect(() => {
        const selectedItems = items.filter((_, index) => checkedItems[index]);
        props.onSelectedItemsChange(selectedItems);
    }, [checkedItems]);

    const itemSorter = (a: any, b: any) => {
        if (a[filterColumn] > b[filterColumn]) {
            return 1 * filterOrder;
        } else if (a[filterColumn] < b[filterColumn]) {
            return -1 * filterOrder;
        } else {
            return 0;
        }
    };

    const onSortClick = (columnName: string) => {
        if (columnName !== filterColumn) {
            updateFilterColumn(columnName);
            updateFilterOrder(1);
        } else {
            updateFilterOrder(filterOrder * -1);
        }
    };

    const handleHeaderCheckboxChange = () => {
        const newCheckedStatus = !allChecked;
        setAllChecked(newCheckedStatus);
        const newCheckedItems = items.reduce((acc, item, index) => {
            acc[index] = newCheckedStatus;
            return acc;
        }, {} as { [key: number]: boolean });
        setCheckedItems(newCheckedItems);
    };

    const handleRowCheckboxChange = (index: number) => {
        const newCheckedItems = { ...checkedItems, [index]: !checkedItems[index] };
        setCheckedItems(newCheckedItems);

        setAllChecked(Object.values(newCheckedItems).every((checked) => checked));
    };
        
    return (
        <Table className="sortable-table" hover responsive>
            <thead>
                <tr className={getOrderCssClass(3)}>
                    <th>
                        <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={handleHeaderCheckboxChange}
                        />
                    </th>
                    <th onClick={() => onSortClick('brokerFirmName')}>
                        {Translate('invoice/customers/header/broker-firm-name')}
                        {filterColumn === 'brokerFirmName' && (
                            <i className="material-icons">
                                {filterOrder === 1 ? 'arrow_drop_down' : 'arrow_drop_up'}
                            </i>
                        )}
                    </th>
                    <th onClick={() => onSortClick('orderCount')}>
                        {Translate('invoice/customers/header/order-count')}
                        {filterColumn === 'orderCount' && (
                            <i className="material-icons">
                                {filterOrder === 1 ? 'arrow_drop_down' : 'arrow_drop_up'}
                            </i>
                        )}
                    </th>
                    <th onClick={() => onSortClick('invoiceBillingMethod')}>
                        {Translate('invoice/customers/header/billing-method')}
                        {filterColumn === 'invoiceBillingMethod' && (
                            <i className="material-icons">
                                {filterOrder === 1 ? 'arrow_drop_down' : 'arrow_drop_up'}
                            </i>
                        )}
                    </th>
                    <th />
                </tr>
            </thead>
            <tbody>
                {props.pendingInvoices.map((pendingInvoice: any, index: number) => (
                    <PendingInvoiceListRow
                        tryagain={props.tryInvoiceAgain}
                        invoiceId={pendingInvoice.id}
                        key={index}
                        brokerFirmName={pendingInvoice.brokerFirmName}
                        invoiceBillingMethod={pendingInvoice.invoiceBillingMethod}
                        orderCount={0}
                        status={pendingInvoice.creationStatus}
                        site={pendingInvoice.site}
                    />
                ))}
                {items.sort(itemSorter).map((invoice: any, index: number) => (
                    <CustomerInvoicablesListRow
                        key={index}
                        brokerFirmName={invoice.brokerFirmName}
                        orderCount={invoice.orderCount}
                        invoiceBillingMethod={invoice.invoiceBillingMethod}
                        invoiceInterval={invoice.invoiceInterval}
                        showInvoice={() =>
                            props.showInvoice(invoice.brokerFirmId, invoice.invoiceReceiverCheckSum)
                        }
                        site={invoice.site}
                        checked={checkedItems[index] || false}
                        onCheckboxChange={() => handleRowCheckboxChange(index)}
                    />
                ))}
            </tbody>
        </Table>
    );
};

export default CustomerInvoicablesList;
